import type { FuelLogEnriched, FuelPlatformOverview, FuelPopularityMetrics, FuelUserSummary, YearlySpendBreakdown } from "@/lib/fuel-analytics";
import { carTitle, userDisplayName } from "@/lib/utils";

function escapeCsv(value: string | number | null | undefined): string {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function downloadBlob(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function exportFuelLogsCsv(logs: FuelLogEnriched[], filename = "fuel-logs.csv") {
  const headers = [
    "loggedAt",
    "user",
    "userID",
    "car",
    "carID",
    "station",
    "stationID",
    "fuelGrade",
    "litres",
    "totalCost",
    "pricePerLitre",
    "inputMode",
    "odometer",
    "currency",
    "notes",
  ];

  const rows = logs.map((log) =>
    [
      log.loggedAt,
      log.user ? userDisplayName(log.user) : "",
      log.userID,
      log.car ? carTitle(log.car) : "",
      log.carID,
      log.station?.name ?? "",
      log.gasStationID,
      log.fuelGrade,
      log.litres,
      log.totalCost,
      log.pricePerLitre,
      log.inputMode,
      log.odometer,
      log.currency,
      log.notes,
    ]
      .map(escapeCsv)
      .join(",")
  );

  downloadBlob(filename, [headers.join(","), ...rows].join("\n"), "text/csv;charset=utf-8");
}

export function exportFuelUserSummariesCsv(
  summaries: FuelUserSummary[],
  filename = "fuel-user-summaries.csv"
) {
  const headers = [
    "user",
    "userID",
    "fillUps",
    "totalSpent",
    "totalLitres",
    "avgPricePerLitre",
    "avgFillCost",
    "lastLoggedAt",
    "topGrade",
    "carCount",
  ];

  const rows = summaries.map((summary) =>
    [
      summary.user ? userDisplayName(summary.user) : "",
      summary.userID,
      summary.fillUps,
      summary.totalSpent,
      summary.totalLitres,
      summary.avgPricePerLitre,
      summary.avgFillCost,
      summary.lastLoggedAt,
      summary.topGrade,
      summary.carIds.length,
    ]
      .map(escapeCsv)
      .join(",")
  );

  downloadBlob(filename, [headers.join(","), ...rows].join("\n"), "text/csv;charset=utf-8");
}

export function exportFuelInsightsJson(
  overview: FuelPlatformOverview,
  insights: { id: string; tone: string; title: string; detail: string }[],
  filename = "fuel-insights.json",
  popularity?: FuelPopularityMetrics,
  yearlyBreakdown?: YearlySpendBreakdown
) {
  downloadBlob(
    filename,
    JSON.stringify(
      { exportedAt: new Date().toISOString(), overview, popularity, yearlyBreakdown, insights },
      null,
      2
    ),
    "application/json;charset=utf-8"
  );
}
